import { Platform } from '@angular/cdk/platform';
import { AfterViewInit, Component, HostListener, Injector, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import moment from 'moment';
import { Subscription } from 'rxjs';

import { IElementsVisibilitySymbolMenuConfig } from '@c/shared/symbol-menu/symbol-menu.model';
import { StrategiesNames } from '@c/top-menu/top-navigation-menu/top-navigation-menu.model';
import {
  CAExchangesForGoogleSearch,
  ChartTabs,
  EasternTimeZoneName,
  ExchangeCountriesCodes,
  Features,
  TabletMinWidth,
  TradePositions,
  USExchangesForGoogleSearch,
  UserSettings,
  formatDecimal,
  formatNumber,
  getTradePositionName,
} from '@const';
import { PositionSizingPopupComponent } from '@m/common/position-sizing-popup/position-sizing-popup.component';
import { AIChatTriggerModel } from '@mod/ai-chats/pxo-ai-chat-triggers.model';
import { PositionSizingModel } from '@mod/data/position-sizing.model';
import { DialogsService } from '@s/common';
import { EarningsService } from '@s/earnings.service';
import { JobDataService } from '@s/job-data.service';
import { ObservableService } from '@s/observable.service';
import { SearchPopupService } from '@s/search-popup.service';
import { SecurityDataDetailsService } from '@s/security-data-details.service';
import { ISymbol, SymbolsService } from '@s/symbols.service';
import { UserDataService } from '@s/user-data.service';
import { getRiskAmountPerTrade as getRiskAmountPerTradeImported } from '@u/business-logic-utils';

@Component({
  selector: 'app-powerx',
  templateUrl: './powerx.component.html',
  styleUrls: ['./powerx.component.scss'],
  standalone: false,
})
export class PowerxComponent implements OnInit, AfterViewInit, OnDestroy {
  public showMaintenance = false;
  public width: number = window.innerWidth;
  protected userSetting = {};
  protected user = { first_name: '', last_name: 'undefined', theme: 1, show_mm_trades: 0, access_type: 1 };

  protected showOrderPanel = false;
  protected readonly isLoading = signal(true);

  protected readonly accountSize = toSignal(this.observableService.accountSize, { injector: this.injector });
  protected readonly accountRiskPercent = toSignal(this.observableService.accountRiskPercent, {
    injector: this.injector,
  });

  protected readonly accountSizeFormattedValues = computed(() => {
    const accountSize = this.accountSize();
    const accountRiskPercent = this.accountRiskPercent();

    return {
      accountSizeFormatted: formatNumber(accountSize),
      accountRiskPercentFormatted: formatDecimal(accountRiskPercent, 1),
      riskAmountPerTradeFormatted: formatNumber(getRiskAmountPerTradeImported(accountSize, accountRiskPercent)),
    };
  });

  protected readonly currentSymbolDetails = signal<ISymbol | null>(null);
  protected readonly analyzeTriggerDetails = computed<AIChatTriggerModel>(() => {
    const currentSymbolDetails = this.currentSymbolDetails();

    if (!currentSymbolDetails) {
      return null;
    }

    return {
      name: 'technical_chart_analysis',
      options: {
        strategy: 'PowerX',
        ticker: currentSymbolDetails.symbol,
        country_code: currentSymbolDetails.country_code,
      },
    };
  });

  protected readonly chartTabs = ChartTabs;
  protected readonly features = Features;

  protected isMobileOS;
  public isLaptop = false;
  public isMobile = false;
  public laptopWidth = 992;
  public mobileWidth = 768;
  private readonly platform: Platform;
  tradePosition = '';
  TradePositions = TradePositions;
  getTradePositionName = getTradePositionName;
  public symbolId$ = this.observableService.symbol;
  selectedSymbol: ISymbol = null;
  public isShowNewsButton = false;
  public exchangeCodeForGoogleSearch;
  selectedSymbolDetails = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lastUpdated: any = '';
  nextEarnings = null;

  public powerxTabsVisibilityConfig: Partial<IElementsVisibilitySymbolMenuConfig> = {
    showOptionsTab: true,
  };
  public currentChartTab: string = ChartTabs.Chart;
  public ChartTabs = ChartTabs;
  public isSymbolMenuOpened = false;
  public strategiesNames = StrategiesNames;

  private subscriptions = new Subscription();

  constructor(
    private dialogsService: DialogsService,
    private searchPopupService: SearchPopupService,
    private observableService: ObservableService,
    private dialog: MatDialog,
    private userDataService: UserDataService,
    private symbolsService: SymbolsService,
    private jobDataService: JobDataService,
    private securityDataDetailsService: SecurityDataDetailsService,
    private earningsService: EarningsService,
    private injector: Injector,
  ) {}

  // @HostListener('window:keyup', ['$event'])
  // onCharsInput(e: KeyboardEvent): void {
  //   const element = e.target as HTMLElement;

  //   if (element && element.hasAttribute('data-ignore-search-popup')) {
  //     return;
  //   }

  //   const isSymbol = e.key?.length === 1 && !!e.key?.match(/[a-z]/i);

  //   if (isSymbol) {
  //     this.searchPopupService.openPopup(e.key, true, false, false, allExchangeCountryCodes);
  //   }
  // }

  @HostListener('window:resize', ['$event']) onResize(event): void {
    this.width = event.target.innerWidth;
    this.checkDeviceWidth();
    this.checkSymbolTabForDesktop();

    this.handlePinnedOrderModal();
  }

  async ngOnInit(): Promise<void> {
    this.showMaintenance = this.observableService.showMaintenance.getValue();
    this.handleShowOrderPanel();
    this.checkIsMobileOS();
    this.checkDeviceWidth();

    this.subscriptions.add(
      this.observableService.showMaintenance.subscribe((showMaintenance) => (this.showMaintenance = showMaintenance)),
    );

    this.subscriptions.add(
      this.observableService.tradingPanelOrderModalPinned.subscribe(() => this.handleShowOrderPanel()),
    );

    this.subscriptions.add(this.observableService.tradingPanelOrderInput.subscribe(() => this.handleShowOrderPanel()));
    this.subscriptions.add(this.observableService.mySettings.subscribe((userSettings) => (this.user = userSettings)));

    this.subscriptions.add(
      this.observableService.tradePosition.subscribe((tradePosition) => (this.tradePosition = tradePosition)),
    );

    this.currentChartTab = this.observableService.chartTab.getValue();

    const { collapsed } = this.observableService.powerxSymbolMenuOptions.getValue();
    this.isSymbolMenuOpened = collapsed;

    const symbol = this.observableService.symbol.getValue();

    this.subscriptions.add(
      this.observableService.symbol.subscribe(async (newSymbol) => await this.loadData(newSymbol)),
    );

    await this.loadData(symbol);
  }

  ngAfterViewInit(): void {
    // need to show preloader for a some time
    setTimeout(() => {
      this.isLoading.set(false);
    }, 100);
  }

  ngOnDestroy(): void {
    this.observableService.tradeReportTradingStrategyId.next(null);
    this.subscriptions.unsubscribe();
  }

  get updateTime(): string {
    if (!this.lastUpdated?.updated_date) return 'N/A';
    return moment(this.lastUpdated?.updated_date).tz('America/New_York').format('MMM D, YYYY h:mm A [ET]');
  }

  get nextEarningsInDays(): number | null {
    return this.nextEarnings?.earnings_in_days;
  }

  get nextEarningsDate(): string {
    if (!this.nextEarnings) return null;
    const formatedDate = moment(this.nextEarnings.report_date).tz(EasternTimeZoneName).format('MMM D, YYYY');
    switch (this.nextEarnings.earnings_in_days) {
      case 0:
        return `Earnings Today on ${formatedDate} ET`;
      case 1:
        return `Earnings in 1 day on ${formatedDate} ET`;
      default:
        return `Earnings in ${this.nextEarnings.earnings_in_days} days on ${formatedDate} ET`;
    }
  }

  handleShowOrderPanel(): void {
    const isPinned = this.observableService.tradingPanelOrderModalPinned.getValue();
    const orderInput = this.observableService.tradingPanelOrderInput.getValue();

    this.showOrderPanel = isPinned && !!orderInput;
  }

  private handlePinnedOrderModal(): void {
    if (!this.showOrderPanel || this.width > TabletMinWidth) {
      return;
    }

    this.observableService.tradingPanelOrderModalPinned.next(false);

    const orderInput = this.observableService.tradingPanelOrderInput.getValue();
    this.dialogsService.openTradingPanelOrderDialog(orderInput);
  }

  protected positionSizing(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = ['position-sizing', 'modals'];
    dialogConfig.disableClose = true;

    dialogConfig.data = {
      accountSizeSettingName: UserSettings.AccountSize,
      accountRiskPercentSettingName: UserSettings.AccountRiskPercent,
      accountRiskAmountSettingName: UserSettings.AccountRiskAmount,
      showCanadaWarning: true,
    } as Partial<PositionSizingModel>;

    this.dialog.open(PositionSizingPopupComponent, dialogConfig);

    document.getElementsByClassName('modals')[0].scrollTo(0, 0);
  }

  protected async openPrintModal(): Promise<void> {
    await this.dialogsService.openPrintDataDialog((afterClosedData) => {
      if (afterClosedData) {
        this.observableService.isNeedPrint$.next(true);
      }
    });
  }

  protected checkIsMobileOS(): void {
    const isAndroid = this.platform?.ANDROID;
    const isIOS = this.platform?.IOS || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    this.isMobileOS = isAndroid || isIOS;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async updatePosition(tradePosition) {
    await this.userDataService.set(UserSettings.TradePosition, tradePosition);
  }

  async loadData(symbol): Promise<void> {
    const [selectedSymbol, selectedSymbolDetails, lastUpdated, nextEarnings] = await Promise.all([
      this.symbolsService.getById(symbol),
      this.securityDataDetailsService.get(symbol),
      this.jobDataService.getRecentSecurity(symbol),
      this.earningsService.getNext(symbol),
    ]);

    this.currentSymbolDetails.set(selectedSymbol);

    this.selectedSymbol = selectedSymbol;
    this.selectedSymbolDetails = selectedSymbolDetails;
    this.lastUpdated = lastUpdated;
    this.nextEarnings = nextEarnings;
    this.showNewsButton();
    this.getExchangeForGoogleSearch();
  }

  showNewsButton(): void {
    this.isShowNewsButton =
      this.selectedSymbol?.country_code === ExchangeCountriesCodes.US ||
      (this.selectedSymbol?.country_code === ExchangeCountriesCodes.CA &&
        !!CAExchangesForGoogleSearch[this.selectedSymbol?.exchange_code]);
  }

  getExchangeForGoogleSearch(): void {
    if (this.selectedSymbol?.country_code === ExchangeCountriesCodes.US) {
      this.exchangeCodeForGoogleSearch = USExchangesForGoogleSearch[this.selectedSymbol?.exchange_code];
    }
    if (this.selectedSymbol?.country_code === ExchangeCountriesCodes.CA) {
      this.exchangeCodeForGoogleSearch = CAExchangesForGoogleSearch[this.selectedSymbol?.exchange_code];
    }
  }

  openNews(): void {
    window.open(
      `https://www.google.com/finance/quote/${this.selectedSymbol?.symbol}:${this.exchangeCodeForGoogleSearch}`,
      '_blank',
    );
  }

  private checkDeviceWidth(): void {
    this.isMobile = this.width < this.mobileWidth;
    this.isLaptop = this.width > this.mobileWidth && this.width < this.laptopWidth;
  }

  private checkSymbolTabForDesktop(): void {
    if (!this.isMobile && !this.isLaptop && this.currentChartTab === ChartTabs.Signal) {
      this.onChangeChartTab(ChartTabs.Chart);
    }
  }

  public async onChangeChartTab(chartTab: string): Promise<void> {
    this.currentChartTab = chartTab;
    await this.userDataService.set(UserSettings.ChartTab, chartTab);
  }

  public onSymbolMenuToggle(): void {
    this.isSymbolMenuOpened = !this.isSymbolMenuOpened;
    const symbolMenuOptions = { collapsed: this.isSymbolMenuOpened };
    this.observableService.powerxSymbolMenuOptions.next(symbolMenuOptions);
    this.userDataService.set(UserSettings.PowerXSymbolMenuOptions, symbolMenuOptions);
  }

  protected changeSecurityId(securityId: number): void {
    this.symbolId$.next(securityId);
    this.userDataService.set(UserSettings.Symbol, securityId);
  }
}
